import React from 'react'

/**
 * ScreenshotPlaceholder — marks exactly where a screenshot belongs in the
 * documentation, and what it should show.
 *
 * HOW TO ADD A SCREENSHOT (for the content author):
 *   1. Capture the screen described in the dashed box.
 *   2. Save it as a PNG with the EXACT filename shown in the box.
 *   3. Drop the file into:  frontend/src/assets/help/
 *   The next build picks it up automatically — no code changes needed.
 *
 * Props:
 *   @param {string} img         - filename to place in src/assets/help/ (e.g. "upload-page.png")
 *   @param {string} description - what the screenshot should capture (shown in the placeholder)
 *   @param {string} source      - where to find the screen (shown as "Where to find it:" in the box)
 *   @param {string} caption     - caption displayed under the final image
 */

// Eagerly import every image in src/assets/help/. When a file is added there,
// Vite includes it on the next build and it is matched by filename below.
const HELP_IMAGES = import.meta.glob('../assets/help/*.{png,jpg,jpeg,webp,gif}', { eager: true })

function resolveImage(img) {
  if (!img) return null
  const match = Object.entries(HELP_IMAGES).find(([path]) => path.endsWith('/' + img))
  return match ? (match[1].default || match[1]) : null
}

function ScreenshotPlaceholder({ img, description, source, caption }) {
  const src = resolveImage(img)

  // Image is present in src/assets/help/ → render it.
  if (src) {
    return (
      <figure style={{ margin: 'var(--space-4) 0' }}>
        <img
          src={src}
          alt={caption || description || ''}
          style={{
            maxWidth: '100%',
            border: '1px solid var(--color-neutral-300)',
            borderRadius: 'var(--radius-md)',
            display: 'block',
          }}
        />
        {caption && (
          <figcaption style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginTop: 6 }}>
            {caption}
          </figcaption>
        )}
      </figure>
    )
  }

  // No image yet → render an obvious "screenshot needed" marker.
  return (
    <div
      style={{
        margin: 'var(--space-4) 0',
        padding: 'var(--space-4)',
        border: '2px dashed var(--color-primary)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-primary-pale)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '1.6rem', marginBottom: 6 }}>📷</div>
      <div style={{ fontWeight: 700, color: 'var(--color-primary-dark)', letterSpacing: '0.04em', fontSize: 'var(--font-size-sm)' }}>
        SCREENSHOT NEEDED
      </div>
      <div style={{ marginTop: 8, color: 'var(--color-neutral-800)', fontSize: 'var(--font-size-sm)' }}>
        {description}
      </div>
      {source && (
        <div style={{
          marginTop: 10,
          padding: '6px 10px',
          background: 'rgba(255,255,255,0.6)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-neutral-800)',
          textAlign: 'left',
          display: 'inline-block',
        }}>
          <strong style={{ color: 'var(--color-primary-dark)' }}>Where to find it:</strong> {source}
        </div>
      )}
      {img && (
        <div style={{ marginTop: 10, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
          Save the image as <code>{img}</code> into <code>frontend/src/assets/help/</code>
        </div>
      )}
      {caption && (
        <div style={{ marginTop: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', fontStyle: 'italic' }}>
          Caption: {caption}
        </div>
      )}
    </div>
  )
}

export default ScreenshotPlaceholder
